import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';

import type { BidAskPrices } from '@services/optionService';

interface BidAskMidPickerProps {
  pricing: BidAskPrices | null;
  selectedPrice: number;
  onPriceChange: (price: number) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

type PriceType = 'bid' | 'mid' | 'ask' | 'custom';

export function BidAskMidPicker({
  pricing,
  selectedPrice,
  onPriceChange,
  isLoading = false,
  disabled = false,
}: BidAskMidPickerProps) {
  const [selectedType, setSelectedType] = useState<PriceType>('mid');
  const [customPrice, setCustomPrice] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  // Initialize selectedType only once when component mounts
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!pricing || isLoading || isInitialized) {
      return;
    }

    // Only auto-detect selection type on initial load - never change it afterwards
    const tolerance = 0.01; // 1 cent tolerance

    if (Math.abs(selectedPrice - pricing.bid) < tolerance) {
      setSelectedType('bid');
    } else if (Math.abs(selectedPrice - pricing.mid) < tolerance) {
      setSelectedType('mid');
    } else if (Math.abs(selectedPrice - pricing.ask) < tolerance) {
      setSelectedType('ask');
    } else {
      setSelectedType('custom');
      setCustomPrice(selectedPrice.toFixed(2));
    }

    setIsInitialized(true);
  }, [pricing, selectedPrice, isLoading, isInitialized]);

  const handlePresetClick = (type: PriceType) => {
    if (!pricing || disabled) {
      return;
    }

    let price: number;
    switch (type) {
      case 'bid':
        price = pricing.bid;
        break;
      case 'mid':
        price = pricing.mid;
        break;
      case 'ask':
        price = pricing.ask;
        break;
      default:
        return;
    }

    setSelectedType(type);
    setShowCustomInput(false);
    onPriceChange(price);
  };

  // Update the actual price when pricing changes but preserve the selection type
  useEffect(() => {
    if (!pricing || isLoading || showCustomInput) {
      return;
    }

    // Update the price based on current selection type when prices change
    let newPrice: number;
    switch (selectedType) {
      case 'bid':
        newPrice = pricing.bid;
        break;
      case 'mid':
        newPrice = pricing.mid;
        break;
      case 'ask':
        newPrice = pricing.ask;
        break;
      case 'custom':
        // Don't update custom prices automatically
        return;
      default:
        return;
    }

    // Only update if the price actually changed (avoid unnecessary updates)
    if (Math.abs(selectedPrice - newPrice) > 0.001) {
      onPriceChange(newPrice);
    }
  }, [
    pricing,
    selectedType,
    selectedPrice,
    isLoading,
    showCustomInput,
    onPriceChange,
  ]);

  const handleEditToggle = () => {
    if (!pricing || disabled) {
      return;
    }

    if (showCustomInput) {
      // Closing custom input - keep the custom price that was typed
      // The price was already updated via handleCustomPriceChange, so just hide the input
      setShowCustomInput(false);
    } else {
      // Opening custom input - set default based on current selection
      let defaultPrice: number;
      switch (selectedType) {
        case 'bid':
          defaultPrice = pricing.bid;
          break;
        case 'ask':
          defaultPrice = pricing.ask;
          break;
        case 'custom':
          defaultPrice = selectedPrice;
          break;
        case 'mid':
        default:
          defaultPrice = pricing.mid;
          break;
      }
      setCustomPrice(defaultPrice.toFixed(2));
      setSelectedType('custom');
      setShowCustomInput(true);
    }
  };

  const handleCustomPriceChange = (value: string) => {
    setCustomPrice(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      console.log('[BidAskMidPicker] Custom price changed:', {
        value,
        numValue,
        selectedType,
      });
      onPriceChange(numValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditToggle(); // Close the input
    }
  };

  if (isLoading || !pricing) {
    return (
      <div className="w-full text-lg font-mono px-2 py-2 rounded border bg-surface/50 border-border-subtle text-center text-label">
        {isLoading ? 'Loading...' : '---'}
      </div>
    );
  }

  const buttonClass =
    'px-1 py-1 rounded border font-medium text-xs transition-all duration-200';
  const buttonSelectedClass =
    'bg-green-500/20 border-green-500/40 text-green-400';
  const buttonUnselectedClass =
    'bg-surface border-border-default text-white/70 hover:bg-surface-hover hover:border-border-hover';

  if (showCustomInput) {
    return (
      <div className="grid grid-cols-4 gap-1">
        {/* Custom input takes 3 columns */}
        <input
          type="number"
          value={customPrice}
          onChange={e => handleCustomPriceChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          step="0.01"
          min="0.01"
          className="col-span-3 text-lg font-mono px-2 py-2 rounded border bg-surface border-border-default text-white focus:border-border-hover focus:outline-none"
          placeholder="0.00"
          autoFocus
          data-testid="custom-limit-price-input"
        />
        {/* Edit button takes 1 column */}
        <button
          onClick={handleEditToggle}
          disabled={disabled}
          className={`${buttonClass} ${selectedType === 'custom' ? buttonSelectedClass : buttonUnselectedClass} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} flex items-center justify-center`}
          data-testid="close-custom-price-button"
        >
          <FontAwesomeIcon icon={faEdit} size="sm" />
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-4 gap-1">
      {/* Bid Button */}
      <button
        onClick={() => handlePresetClick('bid')}
        disabled={disabled}
        className={`${buttonClass} ${selectedType === 'bid' ? buttonSelectedClass : buttonUnselectedClass} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid="bid-price-button"
      >
        <div className="text-[9px] text-white/50 uppercase mb-0.5">BID</div>
        <div className="font-semibold text-[11px]">
          ${pricing.bid.toFixed(2)}
        </div>
      </button>

      {/* Mid Button */}
      <button
        onClick={() => handlePresetClick('mid')}
        disabled={disabled}
        className={`${buttonClass} ${selectedType === 'mid' ? buttonSelectedClass : buttonUnselectedClass} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid="mid-price-button"
      >
        <div className="text-[9px] text-white/50 uppercase mb-0.5">MID</div>
        <div className="font-semibold text-[11px]">
          ${pricing.mid.toFixed(2)}
        </div>
      </button>

      {/* Ask Button */}
      <button
        onClick={() => handlePresetClick('ask')}
        disabled={disabled}
        className={`${buttonClass} ${selectedType === 'ask' ? buttonSelectedClass : buttonUnselectedClass} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        data-testid="ask-price-button"
      >
        <div className="text-[9px] text-white/50 uppercase mb-0.5">ASK</div>
        <div className="font-semibold text-[11px]">
          ${pricing.ask.toFixed(2)}
        </div>
      </button>

      {/* Edit Button */}
      <button
        onClick={handleEditToggle}
        disabled={disabled}
        className={`${buttonClass} ${selectedType === 'custom' ? buttonSelectedClass : buttonUnselectedClass} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} flex flex-col items-center justify-center`}
        data-testid="edit-price-button"
      >
        <FontAwesomeIcon icon={faEdit} size="sm" className="mb-0.5" />
        {selectedType === 'custom' && (
          <div className="font-semibold text-[9px]">
            ${selectedPrice.toFixed(2)}
          </div>
        )}
      </button>
    </div>
  );
}
